import * as THREE from 'three'
import type { Ball } from '../physics/ball.js'
import { physicsToThree } from '../view/scene.js'
import { MAX_SHOT_SPEED, MIN_SHOT_SPEED } from '../physics/constants.js'

/** Maximum drag distance in pixels for full power */
const MAX_DRAG_PX = 200

export interface ShotIntent {
  angle: number    // radians
  speed: number    // m/s
}

export class AimController {
  private _isDragging = false
  private _dragStart = { x: 0, y: 0 }
  private _currentMouse = { x: 0, y: 0 }
  private _aimLine: THREE.Line | null = null
  private _scene: THREE.Scene
  private _canvas: HTMLCanvasElement
  private _onShot: (shot: ShotIntent) => void

  constructor(
    scene: THREE.Scene,
    _camera: THREE.Camera,
    canvas: HTMLCanvasElement,
    onShot: (shot: ShotIntent) => void
  ) {
    this._scene = scene
    this._canvas = canvas
    this._onShot = onShot
    this._setupAimLine()
    this._bindEvents()
  }

  private _setupAimLine(): void {
    const mat = new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.6,
    })
    const geo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(0, 0, 0),
    ])
    this._aimLine = new THREE.Line(geo, mat)
    this._aimLine.visible = false
    this._scene.add(this._aimLine)
  }

  private _bindEvents(): void {
    this._canvas.addEventListener('mousedown', (e) => this._onMouseDown(e))
    this._canvas.addEventListener('mousemove', (e) => this._onMouseMove(e))
    this._canvas.addEventListener('mouseup',   (e) => this._onMouseUp(e))
    this._canvas.addEventListener('touchstart', (e) => this._onTouchStart(e), { passive: false })
    this._canvas.addEventListener('touchmove',  (e) => this._onTouchMove(e),  { passive: false })
    this._canvas.addEventListener('touchend',   (e) => this._onTouchEnd(e))
  }

  enable(): void  { this._canvas.style.cursor = 'crosshair' }
  disable(): void {
    this._canvas.style.cursor = 'default'
    this._hideAimLine()
    this._isDragging = false
  }

  private _onMouseDown(e: MouseEvent): void {
    this._isDragging = true
    this._dragStart = { x: e.clientX, y: e.clientY }
    this._currentMouse = { x: e.clientX, y: e.clientY }
  }

  private _onMouseMove(e: MouseEvent): void {
    this._currentMouse = { x: e.clientX, y: e.clientY }
  }

  private _onMouseUp(e: MouseEvent): void {
    if (!this._isDragging) return
    this._isDragging = false
    this._hideAimLine()
    const shot = this._computeShot(e.clientX, e.clientY)
    if (shot) this._onShot(shot)
  }

  private _onTouchStart(e: TouchEvent): void {
    e.preventDefault()
    const t = e.touches[0]
    this._isDragging = true
    this._dragStart = { x: t.clientX, y: t.clientY }
    this._currentMouse = { x: t.clientX, y: t.clientY }
  }

  private _onTouchMove(e: TouchEvent): void {
    e.preventDefault()
    const t = e.touches[0]
    this._currentMouse = { x: t.clientX, y: t.clientY }
  }

  private _onTouchEnd(e: TouchEvent): void {
    if (!this._isDragging) return
    this._isDragging = false
    this._hideAimLine()
    const t = e.changedTouches[0]
    const shot = this._computeShot(t.clientX, t.clientY)
    if (shot) this._onShot(shot)
  }

  private _computeShot(releaseX: number, releaseY: number): ShotIntent | null {
    const dx = releaseX - this._dragStart.x
    const dy = releaseY - this._dragStart.y
    const dragLen = Math.sqrt(dx * dx + dy * dy)

    if (dragLen < 5) return null  // too small a drag — ignore

    // Billiards cue mechanic: drag AWAY from target (pull back the mallet).
    // Shot fires in the OPPOSITE direction of the drag.
    const angle = Math.atan2(-dy, -dx)
    const powerFraction = Math.min(dragLen / MAX_DRAG_PX, 1)
    const speed = MIN_SHOT_SPEED + (MAX_SHOT_SPEED - MIN_SHOT_SPEED) * powerFraction

    return { angle, speed }
  }

  /**
   * Call each frame while aiming to update the aim line visual.
   */
  updateAimLine(ball: Ball): void {
    if (!this._isDragging || !this._aimLine) return

    const dx = this._currentMouse.x - this._dragStart.x
    const dy = this._currentMouse.y - this._dragStart.y
    const dragLen = Math.sqrt(dx * dx + dy * dy)
    if (dragLen < 2) return

    const angle = Math.atan2(dy, dx)   // same formula as _computeShot
    const power = Math.min(dragLen / MAX_DRAG_PX, 1)

    // Draw aim line from ball in shot direction
    const lineLen = 1 + power * 4  // gets longer with more power
    const start = physicsToThree(ball.pos.x, ball.pos.y, ball.radius + 0.01)
    const end = physicsToThree(
      ball.pos.x + Math.cos(angle) * lineLen,
      ball.pos.y + Math.sin(angle) * lineLen,
      ball.radius + 0.01
    )

    const positions = this._aimLine.geometry.attributes['position'] as THREE.BufferAttribute
    positions.setXYZ(0, start.x, start.y, start.z)
    positions.setXYZ(1, end.x, end.y, end.z)
    positions.needsUpdate = true
    this._aimLine.visible = true
  }

  private _hideAimLine(): void {
    if (this._aimLine) this._aimLine.visible = false
  }

  dispose(): void {
    this._canvas.removeEventListener('mousedown', this._onMouseDown.bind(this))
    this._canvas.removeEventListener('mousemove', this._onMouseMove.bind(this))
    this._canvas.removeEventListener('mouseup',   this._onMouseUp.bind(this))
    if (this._aimLine) {
      this._scene.remove(this._aimLine)
    }
  }
}
